import { Component, OnInit, AfterViewInit, OnDestroy, PLATFORM_ID, Inject, Input, Output, EventEmitter, NgZone } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { environment } from '../../../../environments/environment';
// ngrx store
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

// services
import { PriceService } from '../../../booking/services/price.service';
import { ErrorHandler } from '../../services/error-handler.service';
// models
import { AppState } from '../../../booking/models/app-state.interface';
import { UserInitState } from '../../models/user-reducer.interface';
import { ModalData } from '../../../shared/models/modal.interface';

declare const window: any;
interface PromoValidRequest {
  promoCode: string;
  currency: string;
}

@Component({
  selector: 'app-promo',
  templateUrl: './promo.component.html',
  styleUrls: ['./promo.component.scss']
})
export class PromoComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() price;
  @Input() user: UserInitState;
  @Input() togglePromo;
  @Input() selectedLanguage;
  @Input() selectedLanguageCode;
  @Input() appCopy;
  @Input() screenName;

  @Output() checkPromoChild: EventEmitter<any> = new EventEmitter();

  private _promoSubscription: Subscription;
  private _bookingPromoErrorSubscription: Subscription;
  private _priceErrorSubscription: Subscription;
  private _copySubscription: Subscription;
  private _urlMGM = environment.paymentMgmUrl;
  private _node;
  private _lastSelectedLanguage

  public promoPattern = /^\S*$/;
  public showPromoLoader = false;
  public userPromo;
  public promoErrorMessage: string = '';
  private _promoErrorKey: string = '';
  private _promoErrorParams: any = {};

  constructor(
    private _appStore: Store<AppState>,
    private _pricingService: PriceService,
    private _errorService: ErrorHandler,
    private _zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit() {

    console.log(this.price, this.user, this.togglePromo, this.selectedLanguage, this.appCopy);

    this._promoSubscription = this._pricingService.updatePromoSubject$
      .subscribe(async promo => {
        this._zone.run(async () => {
          this.showPromoLoader = false;
          if (promo.error) {
            const translationErrors = promo.error?.translationResponse?.translationValidationErrors;
            if (translationErrors?.length > 0) {
              this._promoErrorKey = translationErrors[0].errorMessage;
              this._promoErrorParams = translationErrors[0].parameters || {};
            } else {
              this._promoErrorKey = 'errorTextPromoCodeInvalid';
              this._promoErrorParams = {};
            }
            this.resolvePromoErrorMessage();
            return;
          }else {
            this._promoErrorKey = '';
            this._promoErrorParams = {};
            this.promoErrorMessage = '';
          }
          this._pricingService.getPrice();
        });
      });

    this._bookingPromoErrorSubscription = this._errorService.bookingPromoError$
      .subscribe(promoErr => {
        if (promoErr) {
          const setPromo = this.price?.promoCodesApplied[0];
          this.handleRemovePromo(setPromo);
        }
      });

    this._priceErrorSubscription = this._pricingService.priceErrorSubject$
      .subscribe(err => {
        this._zone.run(() => {
          if (err.error) {
            if (typeof err.error.validationErrorMessages === 'undefined' || err.error.validationErrorMessages === null) {
              return;
            } else {
              if (err.error.validationErrorMessages[0].code === 920) {
                this._appStore.dispatch({ type: 'REMOVE_PROMO', payload: [] });
              }
            }
          }
        });
      });

    this._copySubscription = this._appStore.select('copyReducer')
      .subscribe(copy => {
        if (copy.languageCode !== this._lastSelectedLanguage) {
          this._lastSelectedLanguage = copy.languageCode;
          // re-resolve promo error message on language change
          if (this._promoErrorKey) {
            this.resolvePromoErrorMessage();
          }
          // this.removeExternalScript('memtionme');
          // this.removeElementChildNodes('mmWrapper');
          // this.buildMGMUrl();
        }
      });

    // if has return session
    // and promo is not null
    // pass into price
    if (this.getState(this._appStore).flightFinderReducer.returnSessionString !== null) {
      const parsedSession = JSON.parse(this.getState(this._appStore).flightFinderReducer.returnSessionString);
      if (parsedSession.promoCode !== null) {
        const val = { PromoCode: parsedSession.promoCode };
        this.checkPromoEvent(val);
      }
    }

  }

  resolvePromoErrorMessage() {
    if (!this._promoErrorKey) {
      this.promoErrorMessage = '';
      return;
    }
    // Use ErrorHandler's getTranslationByKey — same pattern as sticky error handler
    let message = this._errorService.getTranslationByKey(this._promoErrorKey);
    // Handle parameter substitution (e.g. {paramName} placeholders)
    const changeAbles = Object.keys(this._promoErrorParams);
    if (changeAbles.length > 0) {
      for (const param of changeAbles) {
        const placeholder = '{' + param + '}';
        const paramText = this._promoErrorParams[param]
          ? this._errorService.getTranslationByKey(this._promoErrorParams[param])
          : this._promoErrorParams[param];
        message = message.replace(placeholder, paramText);
      }
    }
    this.promoErrorMessage = message;
  }

  // get store state
  getState(store: Store<AppState>): AppState {
    let state: AppState;
    store.pipe(take(1))
      .subscribe(s => state = s);
    return state;
  }

  checkPromoEvent(value) {
    // if user redeemed miles refund it
    const milesRedeemed = this.getState(this._appStore).milesAndMoreReducer.milesRedeemed;
    if (milesRedeemed) {
      const modalData: ModalData = {
        type: 'default',
        modalType: 'milesRelogin',
        title: this.appCopy.panelHeader['alertModalLogin'+this.selectedLanguage],
        content: this.appCopy.panelHeader['milesPromoModalTitle'+this.selectedLanguage],
        buttonText: this.appCopy.panelHeader['alertOKButton'+this.selectedLanguage],
        cancelButtonText: this.appCopy.panelHeader['alertCancelButton'+this.selectedLanguage],
      };

      this._errorService.handleModal(modalData);
      return;
    }
    if (isPlatformBrowser(this.platformId)) {
      if (!this.user?.userDetails.userLoggedIn) {
        const state = this.getState(this._appStore);
        const emailEl = (<HTMLInputElement>document.getElementById('bookerEmail'));
        let bookerEmail = emailEl ? emailEl.value : '';
        if (state.bookingReducer.bookingDetails.isD2dService && state.d2dReducer.addBookingCount > 0) {
          bookerEmail = state.d2dReducer.formData.bookerForm.bookerEmail;
        }
        if (bookerEmail !== '' || bookerEmail !== null) {
          this._appStore.dispatch({ type: 'UPDATE_USER_EMAIL', payload: bookerEmail });
        }
      }
    }

    if (value.PromoCode.trim() === '') { return; }
    this.promoErrorMessage = '';
    this.showPromoLoader = true;
    const storeData = this.getState(this._appStore);
    const token = storeData.userReducer.userDetails.userToken;
    const currency = storeData.timeslotReducer.currency.code;
    const promoRequest: PromoValidRequest = { promoCode: value.PromoCode.trim(), currency: currency};
    this._pricingService.checkIfPromoValid(promoRequest, token);
  }

  handleRemovePromo(code) {
    const matchCase = code === null ? '' : code.toUpperCase();
    this._pricingService.removeSetPromo(matchCase);
  }

  buildMGMUrl() {
    const lanaguageCode = localStorage.getItem('languageCode') ?? 'en';
    const locales = { 'en': 'en_GB', 'fr': 'fr_FR', 'de': 'de_DE', 'it': 'it_IT' };
    const url = new URL(this._urlMGM);
    const searchParams = url.searchParams;
    searchParams.set('locale', locales[lanaguageCode])
    if (typeof this.user.userDetails.currentUser.emailAddress !== 'undefined') {
      searchParams.set('email', this.user.userDetails.currentUser.emailAddress);
      // this._urlMGM += '&email=' + this.user.userDetails.currentUser.emailAddress;
    }

    if (isPlatformBrowser(this.platformId)) {
      url.search = searchParams.toString();
      this.injectExternalScript(url.toString());
    }
  }

  injectExternalScript(url) {
    this._node = document.createElement('script');
    this._node.src = url;
    this._node.type = 'text/javascript';
    this._node.id = 'memtionme-script';
    document.getElementsByTagName('app-step-payment')[0].appendChild(this._node);
  }

  removeExternalScript(id: string) {
    if (isPlatformBrowser(this.platformId)) {
      /* This will ensure the link (implementation) and checkout (situation) get
        cleared before loading the Mention Me tag shown on refresh or page change.
      */
      if (window.MentionMeFiredTags != undefined && window.MentionMeFiredTags != '') {
          var situation = 'checkout';
          var implementation = 'link';

          if (window.MentionMeFiredTags[implementation + situation] == true) {
              delete window.MentionMeFiredTags[implementation + situation]
          }
      }
      // Create Element.remove() function if not exist
      if (!('remove' in Element.prototype as any)) {
        Element.prototype.remove = function() {
            if (this.parentNode) {
              this.parentNode.removeChild(this);
            }
        };
      }

      if (document.getElementById(id + '-script')) {
        document.getElementById(id + '-script').remove();
      }
    }
  }

  removeElementChildNodes(id: string) {
    if (isPlatformBrowser(this.platformId)) {
      if (document.getElementById(id)) {
        document.getElementById(id).innerHTML = '';
      }
    }
  }

  ngAfterViewInit(): void {
    const state = this.getState(this._appStore);
    if (state.d2dReducer.promoCode.length > 0 && state.bookingReducer.bookingDetails.isD2dService) {
      this.userPromo = state.d2dReducer.promoCode[0];
      const val = { PromoCode: this.userPromo };
      this.checkPromoEvent(val);
    }
  }

  ngOnDestroy() {
    if (this._promoSubscription) { this._promoSubscription.unsubscribe(); }
    if (this._bookingPromoErrorSubscription) { this._bookingPromoErrorSubscription.unsubscribe(); }
    if (this._priceErrorSubscription) { this._priceErrorSubscription.unsubscribe(); }
  }

}
